import React from "react";
import { useTranslation } from "utilities/i18n";
import {
  KeyBucket,
  Address,
  Site,
  useDevicesForTenantQuery,
  useSitesAndRoomsQuery,
  useTenantQuery,
  Maybe
} from "generated/graphql";
import { Layout } from "views/Main/Layouts";
import { Card } from "collections/Cards";
import { Section } from "collections/Section";
import { Loader } from "elements";
import { TenantType } from "generated/graphql.schema";

export const useAccountQuery = (id: string) => {
  const { data, executor } = useTenantQuery({
    variables: {
      id
    }
  });
  return {
    tenant: data?.tenant ?? {
      id,
      name: null,
      type: TenantType.Enterprise,
      mockLicenseTenant: false
    },
    executor
  };
};

const Column: React.FC<{ width: number; end?: boolean; vcenter?: boolean }> = ({
  width,
  children,
  vcenter,
  end
}) => {
  const classes = [`col-${width}`, end ? "end" : "", vcenter ? "vcenter" : ""]
    .filter(c => !!c)
    .join(" ");
  return <div className={classes}>{children}</div>;
};

const DevicesSection: React.FC<{ routeId: string }> = ({ routeId }) => {
  const { t } = useTranslation();
  const { tenant } = useAccountQuery(routeId);
  const { Header, Content } = Section;
  const { data, fetching } = useDevicesForTenantQuery({
    variables: {
      tenantId: tenant.id
    }
  });
  const models =
    data?.models
      .flatMap(({ buckets }) => buckets)
      .filter(
        (bucket): bucket is KeyBucket => bucket.__typename === "KeyBucket"
      )
      .sort((a, b) => b.count - a.count) ?? [];
  const count = data?.deviceCount ?? 0;
  return (
    <Section fluid>
      <Header data-testid="device-count">
        {t("ACCOUNT_DEVICES", { count })}
      </Header>
      <Content>
        <Loader loading={fetching}>
          <Card className="table-card" fluid>
            {models.map(({ count, key }) => {
              return (
                <div className="grid stretched" key={key}>
                  <Column width={6}>
                    <div className="h2">{count}</div>
                  </Column>
                  <Column width={6} vcenter>
                    {key}
                  </Column>
                </div>
              );
            })}
          </Card>
        </Loader>
      </Content>
    </Section>
  );
};

type SiteEntryProps = Pick<
  Site,
  "id" | "name" | "deviceCount" | "roomCount"
> & { address: Maybe<Pick<Address, "city" | "region" | "country">> };
const SiteEntry: React.FC<SiteEntryProps> = ({
  id,
  name,
  deviceCount,
  roomCount,
  address
}) => {
  const { t } = useTranslation();
  let location =
    address?.city ??
    address?.region ??
    address?.country ??
    t("MISSING_VALUE_PLACEHOLDER");
  return (
    <React.Fragment key={id}>
      <Column width={5}>
        <p>{name}</p>
      </Column>
      <Column width={1}>
        <p>{deviceCount}</p>
      </Column>
      <Column width={1}>
        <p>{roomCount}</p>
      </Column>
      <Column width={5}>
        <p>{location}</p>
      </Column>
    </React.Fragment>
  );
};
const SitesSection: React.FC<{ routeId: string }> = ({ routeId }) => {
  const { t } = useTranslation();
  const { Header, Content } = Section;
  const { data, fetching } = useSitesAndRoomsQuery({
    variables: {
      tenantId: routeId
    }
  });

  const siteCount = data?.sites.length ?? 0;
  const sites = data?.sites ?? [];

  return (
    <Section fluid>
      <Header data-testid="site-count">
        {t("ACCOUNT_SITES", { count: siteCount ?? 0 })}
      </Header>
      <Content>
        <Card className="table-card" fluid>
          <div className="grid">
            <Column width={5}>
              <label>{t("SITE")}</label>
            </Column>
            <Column width={1}>
              <label>{t("DEVICES")}</label>
            </Column>
            <Column width={1}>
              <label>{t("ROOMS")}</label>
            </Column>
            <Column width={5}>
              <label>{t("LOCATION")}</label>
            </Column>
            <Loader loading={fetching}>
              {sites.map(site => (
                <SiteEntry key={site.id} {...site} />
              ))}
            </Loader>
          </div>
        </Card>
      </Content>
    </Section>
  );
};

export const AccountDetails: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  const { tenant } = useAccountQuery(id);
  const { id: tid, name } = tenant;
  const { Content } = Layout;
  return (
    <>
      <Content scrolling>
        <Content.Title className="card title-card" nested>
          {t("ACCOUNT_DETAILS", { name })}
        </Content.Title>
        <Content.ScrollingList>
          <div className="grid stretched fitted" style={{ minHeight: 120 }}>
            <Column width={5}>
              <DevicesSection routeId={tid} />
            </Column>
            <Column width={7}>
              <SitesSection routeId={tid} />
            </Column>
          </div>
        </Content.ScrollingList>
      </Content>
    </>
  );
};

export default AccountDetails;
